import { fetchConsultants } from './firebase'
import { List } from 'immutable'

import utilsSearch from 'utils/search'
import suggest from 'utils/suggest'

export const search = (query, available) =>
  Promise.all([fetchConsultants(), tags()]).then(([consultants, tags]) =>
    utilsSearch({
      consultants: consultants.filter(consultant => consultant.listed).toJS(),
      query,
      tags: tags.toArray().filter(t => t.name),
    }).map(match => ({
      ...consultants.get(match.id),
      ...match,
    }))
  )

const consultantExperienceTags = (consultant = {}) =>
  (consultant.experience || []).reduce(
    (tags, experience) => [
      ...tags,
      ...(experience.tags || []),
      experience.employer,
      experience.customer,
      experience.university,
      experience.degree,
    ],
    []
  )

const consultantRoleTags = (consultant = {}) =>
  (consultant.experience || []).reduce(
    (tags, experience) => [...tags, ...(experience.roles || [])],
    []
  )

const consultantTags = (consultant = {}) => [
  ...(consultant.interests || []),
  ...(consultant.titles || []),
  consultant.name,
]

const mapToExperienceTags = consultants =>
  consultants
    .reduce(
      (tags, consultant) => [...tags, ...consultantExperienceTags(consultant)],
      []
    )
    .reduce((tags, newTag) => {
      const index = tags.findIndex(tag => tag.name === newTag)
      return index >= 0
        ? tags.update(index, tag => ({
            ...tag,
            count: tag.count ? tag.count + 1 : 1,
          }))
        : tags.push({
            name: newTag,
            count: 1,
          })
    }, List())

let experienceTagList

const experienceTags = () =>
  new Promise((resolve, reject) => {
    if (experienceTagList) {
      resolve(experienceTagList)
    } else {
      fetchConsultants()
        .then(mapToExperienceTags)
        .then(tags => {
          experienceTagList = tags
          resolve(experienceTagList)
        })
        .catch(reject)
    }
  })

const mapToRoleTags = consultants =>
  consultants
    .reduce(
      (tags, consultant) => [...tags, ...consultantRoleTags(consultant)],
      []
    )
    .reduce((tags, newTag) => {
      const index = tags.findIndex(tag => tag.name === newTag)
      return index >= 0
        ? tags.update(index, tag => ({
            ...tag,
            count: tag.count ? tag.count + 1 : 1,
          }))
        : tags.push({
            name: newTag,
            count: 1,
          })
    }, List())

let roleTagList

const roleTags = () =>
  new Promise((resolve, reject) => {
    if (roleTagList) {
      resolve(roleTagList)
    } else {
      fetchConsultants()
        .then(mapToRoleTags)
        .then(tags => {
          roleTagList = tags
          resolve(roleTagList)
        })
        .catch(reject)
    }
  })

const mapToTags = consultants =>
  consultants
    .reduce(
      (tags, consultant) => [
        ...tags,
        ...consultantTags(consultant),
        ...consultantRoleTags(consultant),
        ...consultantExperienceTags(consultant),
      ],
      []
    )
    .reduce((tags, newTag) => {
      const index = tags.findIndex(tag => tag.name === newTag)
      return index >= 0
        ? tags.update(index, tag => ({
            ...tag,
            count: tag.count ? tag.count + 1 : 1,
          }))
        : tags.push({
            name: newTag,
            count: 1,
          })
    }, List())

let tagList

const tags = () =>
  new Promise((resolve, reject) => {
    if (tagList) {
      resolve(tagList)
    } else {
      fetchConsultants()
        .then(mapToTags)
        .then(tags => {
          tagList = tags
          resolve(tagList)
        })
        .catch(reject)
    }
  })

const mapToInterestTags = consultants =>
  consultants
    .reduce(
      (tags, consultant) => [...tags, ...(consultant.interests || [])],
      []
    )
    .reduce((tags, newTag) => {
      const index = tags.findIndex(tag => tag.name === newTag)
      return index >= 0
        ? tags.update(index, tag => ({
            ...tag,
            count: tag.count ? tag.count + 1 : 1,
          }))
        : tags.push({
            name: newTag,
            count: 1,
          })
    }, List())

let interestTagList

const interestTags = () =>
  new Promise((resolve, reject) => {
    if (interestTagList) {
      resolve(interestTagList)
    } else {
      fetchConsultants()
        .then(mapToInterestTags)
        .then(tags => {
          interestTagList = tags
          resolve(interestTagList)
        })
        .catch(reject)
    }
  })

const mapToCustomerTags = consultants =>
  consultants
    .reduce(
      (tags, consultant) => [
        ...tags,
        ...(consultant.experience || [])
          .reduce((tags, experience) => [...tags, experience.customer], [])
          .filter(t => t),
      ],
      []
    )
    .reduce((tags, newTag) => {
      const index = tags.findIndex(tag => tag.name === newTag)
      return index >= 0
        ? tags.update(index, tag => ({
            ...tag,
            count: tag.count ? tag.count + 1 : 1,
          }))
        : tags.push({
            name: newTag,
            count: 1,
          })
    }, List())

let customerTagList

const customerTags = () =>
  new Promise((resolve, reject) => {
    if (customerTagList) {
      resolve(customerTagList)
    } else {
      fetchConsultants()
        .then(mapToCustomerTags)
        .then(tags => {
          customerTagList = tags
          resolve(customerTagList)
        })
        .catch(reject)
    }
  })

const mapToUniversityTags = consultants =>
  consultants
    .reduce(
      (tags, consultant) => [
        ...tags,
        ...(consultant.experience || [])
          .reduce((tags, experience) => [...tags, experience.university], [])
          .filter(t => t),
      ],
      []
    )
    .reduce((tags, newTag) => {
      const index = tags.findIndex(tag => tag.name === newTag)
      return index >= 0
        ? tags.update(index, tag => ({
            ...tag,
            count: tag.count ? tag.count + 1 : 1,
          }))
        : tags.push({
            name: newTag,
            count: 1,
          })
    }, List())

let universityTagList

const universityTags = () =>
  new Promise((resolve, reject) => {
    if (universityTagList) {
      resolve(universityTagList)
    } else {
      fetchConsultants()
        .then(mapToUniversityTags)
        .then(tags => {
          universityTagList = tags
          resolve(universityTagList)
        })
        .catch(reject)
    }
  })

export const fetchSuggestions = (suggestionType, value) => {
  switch (suggestionType) {
    case 'tag':
      return value.length > 0
        ? experienceTags().then(tags => suggest(value, tags))
        : []
    case 'role':
      return value.length > 0
        ? roleTags().then(tags => suggest(value, tags))
        : []
    case 'interest':
      return value.length > 0
        ? interestTags().then(tags => suggest(value, tags))
        : []
    case 'customer':
      return value.length > 0
        ? customerTags().then(tags => suggest(value, tags))
        : []
    case 'university':
      return value.length > 0
        ? universityTags().then(tags => suggest(value, tags))
        : []
    default:
      return value.length > 0
        ? tags().then(tags => suggest(value, tags))
        : [
            'React',
            'Fullstack',
            'Grafisk designer',
            'Microservices',
            'Frontend',
            'Design Thinking',
            'React Native',
            'AI',
            'Docker',
          ]
  }
}



// WEBPACK FOOTER //
// ./src/api/search.js